import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import api from '../../utils/api';
import CompanyLogo from '../../components/CompanyLogo';

export const meta = {
  id: 'top_companies',
  category: 'tickets',
  titleKey: 'dashboard.widgets.topCompanies',
  descriptionKey: 'dashboard.widgets.topCompaniesDesc',
  defaultSize: 'md',
  icon: Building2,
};

export default function TopCompaniesWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: companies = [] } = useQuery({
    queryKey: ['dashboard-top-companies'],
    queryFn: () => api.get('/dashboard/top-companies', { params: { limit: 6 } }).then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const max = Math.max(1, ...companies.map(c => parseInt(c.open_count) || 0));

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Building2 size={16} />
          {t('dashboard.widgets.topCompanies')}
        </div>
        <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
          {t('dashboard.widgets.topCompaniesHint', 'by open tickets')}
        </span>
      </div>
      {companies.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      ) : (
        <div style={{ padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {companies.map(c => {
            const open = parseInt(c.open_count) || 0;
            const new30 = parseInt(c.new_30d) || 0;
            const pct = Math.max(6, Math.round((open / max) * 100));
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/tickets?company_id=${c.id}&status=__active__`)}
                style={{
                  cursor: 'pointer',
                  padding: '8px 10px',
                  borderRadius: 6,
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(to right, rgba(59, 130, 246, 0.10) ${pct}%, transparent ${pct}%)`,
                  pointerEvents: 'none',
                }} />
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CompanyLogo name={c.name} logoUrl={c.logo_url} size={22} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
                      {new30} {t('dashboard.widgets.topCompaniesNew30d', 'new in 30d')}
                    </div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-brand)', flexShrink: 0 }}>
                    {open}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
